import { normalizeCropGeometryEntry, serializeCropGeometryEntry } from './cropGeometry';

const TRANSFER_FORMAT = 'szkody-lowieckie-crops';
const TRANSFER_VERSION = 4;
const SUPPORTED_TRANSFER_VERSIONS = [1, 2, 3, 4];
const SUPPORTED_GEOMETRY_TYPES = new Set(['Polygon', 'MultiPolygon']);

const isPosition = (coordinates) => (
  Array.isArray(coordinates)
  && coordinates.length >= 2
  && coordinates.every((coordinate) => Number.isFinite(Number(coordinate)))
);

const isLinearRing = (ring) => (
  Array.isArray(ring)
  && ring.length >= 4
  && ring.every(isPosition)
  && ring[0].length === ring[ring.length - 1].length
  && ring[0].every((coordinate, index) => coordinate === ring[ring.length - 1][index])
);

const isValidPolygonCoordinates = (coordinates) => (
  Array.isArray(coordinates)
  && coordinates.length > 0
  && coordinates.every(isLinearRing)
);

const isValidGeometry = (geometry) => {
  if (geometry?.type === 'Polygon') {
    return isValidPolygonCoordinates(geometry.coordinates);
  }
  if (geometry?.type === 'MultiPolygon') {
    return Array.isArray(geometry.coordinates)
      && geometry.coordinates.length > 0
      && geometry.coordinates.every(isValidPolygonCoordinates);
  }
  return false;
};

export const createCropTransferCollection = (
  cropEntries,
  field,
  { aoiGeometry = null, remainingFieldDamagePercent = 0, exclusions = [] } = {}
) => {
  if (aoiGeometry && !isValidGeometry(aoiGeometry)) {
    throw new Error('Nieprawidłowa geometria obrysu pola.');
  }

  const remainingPercent = Number(remainingFieldDamagePercent);
  if (!Number.isFinite(remainingPercent) || remainingPercent < 0 || remainingPercent > 100) {
    throw new Error('Ocena uszkodzenia poza spotami musi być w zakresie 0–100%.');
  }

  const exclusionFeatures = (exclusions || []).map((exclusion, index) => {
    const geometry = exclusion?.geometry || null;
    if (!isValidGeometry(geometry)) {
      throw new Error(`Nieprawidłowa geometria wyłączenia nr ${index + 1}.`);
    }
    return {
      type: 'Feature',
      geometry,
      properties: {
        feature_type: 'exclusion',
        ...(exclusion?.id ? { exclusion_id: exclusion.id } : {}),
      },
    };
  });

  return {
    type: 'FeatureCollection',
    properties: {
      format: TRANSFER_FORMAT,
      version: TRANSFER_VERSION,
      field: {
        id: field.id,
        title: field.title,
      },
    },
    features: [
      ...(aoiGeometry ? [{
        type: 'Feature',
        geometry: aoiGeometry,
        properties: {
          feature_type: 'field_boundary',
          remaining_damage_percent: remainingPercent,
        },
      }] : []),
      ...exclusionFeatures,
      ...cropEntries.map((entry) => {
        const crop = normalizeCropGeometryEntry(entry);
        return {
          type: 'Feature',
          geometry: crop.geometry,
          properties: {
            feature_type: 'crop',
            crop_id: crop.id,
            spot_number: crop.spotNumber,
            damage_percent: crop.damagePercent,
            protected_species: crop.protectedSpecies,
          },
        };
      }),
    ],
  };
};

export const parseCropTransferCollection = (collection) => {
  if (
    !collection
    || collection.type !== 'FeatureCollection'
    || collection.properties?.format !== TRANSFER_FORMAT
    || !SUPPORTED_TRANSFER_VERSIONS.includes(collection.properties?.version)
    || !Array.isArray(collection.features)
  ) {
    throw new Error('Nieprawidłowy format pliku cropów lub nieobsługiwana wersja.');
  }

  const isLegacyFormat = collection.properties.version === 1;
  const supportsSpotNumbers = collection.properties.version >= 3;
  const cropIds = new Set();
  const spotNumbers = new Set();
  const exclusionIds = new Set();
  let aoiGeometry = null;
  let remainingFieldDamagePercent = null;
  const crops = [];
  const exclusions = [];
  collection.features.forEach((feature, index) => {
    if (
      feature?.type !== 'Feature'
      || !SUPPORTED_GEOMETRY_TYPES.has(feature.geometry?.type)
      || !isValidGeometry(feature.geometry)
    ) {
      const featureType = feature?.properties?.feature_type;
      const elementLabel = featureType === 'field_boundary'
        ? 'obrysu pola'
        : featureType === 'exclusion'
          ? 'wyłączenia'
          : 'cropa';
      throw new Error(`Nieprawidłowa geometria ${elementLabel} nr ${index + 1}.`);
    }

    const featureType = isLegacyFormat ? 'crop' : feature.properties?.feature_type;
    if (featureType === 'field_boundary') {
      if (aoiGeometry) {
        throw new Error('Plik zawiera więcej niż jeden obrys pola.');
      }
      const remainingPercent = Number(feature.properties?.remaining_damage_percent);
      if (!Number.isFinite(remainingPercent) || remainingPercent < 0 || remainingPercent > 100) {
        throw new Error('Nieprawidłowa ocena uszkodzenia poza spotami.');
      }
      aoiGeometry = feature.geometry;
      remainingFieldDamagePercent = remainingPercent;
      return;
    }
    if (featureType === 'exclusion') {
      const exclusionId = feature.properties?.exclusion_id || `exclusion-${index + 1}`;
      if (exclusionIds.has(exclusionId)) {
        throw new Error(`Powielone ID wyłączenia: ${exclusionId}.`);
      }
      exclusionIds.add(exclusionId);
      exclusions.push({ id: exclusionId, geometry: feature.geometry });
      return;
    }
    if (featureType !== 'crop') {
      throw new Error(`Nieznany typ elementu w pliku cropów nr ${index + 1}.`);
    }

    const spotNumber = supportsSpotNumbers ? feature.properties?.spot_number : null;
    if (supportsSpotNumbers && (!Number.isInteger(spotNumber) || spotNumber < 1 || spotNumbers.has(spotNumber))) {
      throw new Error(`Nieprawidłowy lub powielony numer spotu nr ${index + 1}.`);
    }

    const crop = serializeCropGeometryEntry(normalizeCropGeometryEntry({
      id: feature.properties?.crop_id,
      spot_number: spotNumber,
      geometry: feature.geometry,
      damage_percent: feature.properties?.damage_percent,
      protected_species: feature.properties?.protected_species,
    }));
    if (cropIds.has(crop.id)) {
      throw new Error(`Powielone ID cropa: ${crop.id}.`);
    }
    cropIds.add(crop.id);
    if (crop.spot_number) {
      spotNumbers.add(crop.spot_number);
    }
    crops.push(crop);
  });

  return { crops, exclusions, aoiGeometry, remainingFieldDamagePercent };
};
